  import React, { useEffect, useState, useRef, forwardRef } from 'react';
  import ReactDOM from 'react-dom';
  import { useDropdown } from './dropdown';

  type DropdownPosition = 'top' | 'bottom' | 'right' | 'left';


  export interface DropdownMenuProps {
    className?: string;
    [others: string]: any;
    placement?: DropdownPosition;
    children?: any;
    renderAsPortal?: boolean,
    overrideStyles?: any,
    }

    const DropdownMenu: React.FC<DropdownMenuProps> = ({
      className,
      children,
      placement = 'bottom',
      renderAsPortal: propRenderAsPortal,
      overrideStyles: propOverrideStyles = null,
      ...rest
    }) => {
      const {
        id,
        showMenu,
        setShowMenu,
        dropdownRef,
        handleClickMenu,
        keepShown,
        onChangeToggleMenu,
        renderAsPortal: hookRenderAsPortal,
        overrideStyles: hookOverrideStyles = null
      } = useDropdown();
      const [theme, setTheme] = useState<string | null>(null);
      const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
      const [menuWidth, setMenuWidth] = useState(0);
      const menuRef = useRef<HTMLUListElement>(null);
    
      useEffect(() => {
        const themes = ['lhh-2024', 'lhh-2025', 'adecco', 'akkodis', 'tagcom'];
    
        const foundTheme = themes.find(theme => document.querySelector(`.${theme}`) !== null);
        if (foundTheme) {
          setTheme(foundTheme);
        }
      }, []);
    
      useEffect(() => {
        if (!dropdownRef.current || !menuRef.current) return;
    
        const handleClickOutside = (event: MouseEvent) => {
          const target = event.target as Node | null;
          if (!dropdownRef.current?.contains(target) && !menuRef.current?.contains(target)) {
            setShowMenu(false);
            if (onChangeToggleMenu) {
              onChangeToggleMenu(false);
            }
          }
        };
    
        if (showMenu) {
          document.addEventListener('click', handleClickOutside);
        }
    
        return () => {
          document.removeEventListener('click', handleClickOutside);
        };
      }, [showMenu, dropdownRef, menuRef]);
    
      useEffect(() => {
        if (!dropdownRef.current) return;
        if (!showMenu) return;
    
        const updateWidth = () => {
          if (dropdownRef.current) {
            setMenuWidth(dropdownRef.current.offsetWidth);
          }
        };
    
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
      }, [showMenu]);
    
      useEffect(() => {
        if (!showMenu) return;
    
        const updatePosition = () => {
          if (!dropdownRef.current || !menuRef.current) return;
    
          const buttonRect = dropdownRef.current.getBoundingClientRect();
          const menuRect = menuRef.current.getBoundingClientRect();
    
          let newTop = 0;
          let newLeft = 0;
    
          switch (placement) {
            case 'top':
              newTop = buttonRect.top + window.scrollY - menuRect.height;
              newLeft = buttonRect.left + window.scrollX;
              break;
            case 'bottom':
              newTop = buttonRect.top + window.scrollY + buttonRect.height;
              newLeft = buttonRect.left + window.scrollX;
              break;
            case 'right':
              newTop = buttonRect.top + window.scrollY;
              newLeft = buttonRect.right + window.scrollX;
              break;
            case 'left':
              newTop = buttonRect.top + window.scrollY;
              newLeft = buttonRect.left + window.scrollX - menuRect.width;
              break;
          }
    
      // Update state only if the calculated position changes to minimize unnecessary re-renders
          if (menuPosition.top !== newTop || menuPosition.left !== newLeft) {
            setMenuPosition({ top: newTop, left: newLeft, width: menuRect.width });
          }
        };
    
        updatePosition();
  
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
    
        return () => {
          window.removeEventListener('resize', updatePosition);
          window.removeEventListener('scroll', updatePosition);
        };
      }, [showMenu, dropdownRef, placement]);
  
    
      const portaStyle: React.CSSProperties = {
        ...((propRenderAsPortal || hookRenderAsPortal) && {
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          width: 'auto',
        })
      };
    
      const styles = { ...portaStyle, ...propOverrideStyles, ...hookOverrideStyles };
    
      const content = (
        <ul
          className={`tag-ds dropdown-menu ${placement ? placement : ''} ${theme ? theme : ''} ${showMenu ? '' : 'hidden'} ${className || ''}`}
          id={`${id || 'dropdown'}-menu`}
          ref={menuRef}
          aria-labelledby={`${id}-btn`}
          onClick={!keepShown ? handleClickMenu : undefined}
          style={(propRenderAsPortal || hookRenderAsPortal) ? styles : { minWidth: '100%' }}
          {...rest}
        >
          {children}
        </ul>
      );
    
      if (showMenu && (propRenderAsPortal || hookRenderAsPortal)) {
        return ReactDOM.createPortal(content, document.body);
      }
      if (showMenu) {
        return content;
      }
      return null;
  };

export default DropdownMenu;